import { isUsaName } from "../epg/categoryOrder.js";
import { countryOfName, isOtherCountry } from "../library/countryTag.js";

/**
 * Which channels Home's "Live Now" rail features (owner, 2026-10-12): it used to take what
 * TVmaze said was airing and match the NETWORK NAME inside any channel name, which put an
 * Italian Serie A event feed in the rail as "Good Morning America" ("Atalanta BC" contains
 * "abc") and NFL team packages as "Today". The rule now:
 *
 *  - only US channels, never a 24/7 loop, never an event or package channel;
 *  - only the most-watched US networks, in a ranked list of our own (TOP_NETWORKS), matched
 *    by the whole channel name, not by a word inside it;
 *  - rotating among them: each half hour the order is shaken, weighted toward the top;
 *  - live sports count double;
 *  - local stations (ABC, CBS, NBC, FOX, CW, PBS affiliates) only for the viewer's own market,
 *    which is read from the stations they favourite and watch, never guessed.
 *
 * "Your country" (owner, 2026-10-20): outside the US the network list and local stations do not
 * apply; a channel counts by its country tag alone (its name's, or its category's), each channel
 * name one tile, the same rotation, no 24/7 loops or event channels. For the US nothing changes.
 *
 * Pure, so it is tested without a screen.
 */

export interface Network {
  id: string;
  /** Normalised whole channel names (see words()) this network is known by. */
  names: readonly string[];
  /** A sports network: a live event on it is live sports. */
  sports?: boolean;
}

const net = (id: string, names: string[], sports = false): Network => ({ id, names, sports });

/**
 * The most-watched US networks, most watched first. Our own estimate of cable and broadcast
 * viewership; the order is the rank the rotation weighs (editorial, change it freely).
 */
export const TOP_NETWORKS: readonly Network[] = [
  net("foxnews", ["fox news", "fox news channel"]),
  net("espn", ["espn"], true),
  net("abc", ["abc"]),
  net("cbs", ["cbs"]),
  net("nbc", ["nbc"]),
  net("fox", ["fox"]),
  net("msnbc", ["msnbc", "ms now", "ms now msnbc", "msnbc ms now"]),
  net("cnn", ["cnn"]),
  net("hgtv", ["hgtv"]),
  net("tnt", ["tnt"]),
  net("usanetwork", ["usa network"]),
  net("discovery", ["discovery", "discovery channel"]),
  net("hallmark", ["hallmark", "hallmark channel"]),
  net("tbs", ["tbs"]),
  net("food", ["food network", "food"]),
  net("history", ["history", "history channel"]),
  net("ae", ["a e", "ae", "a and e"]),
  net("fs1", ["fs1", "fox sports 1", "fox sports1"], true),
  net("nfl", ["nfl network", "nfl"], true),
  net("espn2", ["espn2", "espn 2"], true),
  net("newsmax", ["newsmax", "newsmax tv"]),
  net("newsnation", ["newsnation", "news nation"]),
  net("amc", ["amc"]),
  net("fx", ["fx"]),
  net("bravo", ["bravo"]),
  net("comedy", ["comedy central", "comedy"]),
  net("tlc", ["tlc"]),
  net("id", ["investigation discovery", "id"]),
  net("lifetime", ["lifetime"]),
  net("freeform", ["freeform"]),
  net("paramount", ["paramount network", "paramount"]),
  net("nick", ["nickelodeon", "nick"]),
  net("disney", ["disney channel", "disney"]),
  net("cartoon", ["cartoon network"]),
  net("tcm", ["tcm", "turner classic movies"]),
  net("trutv", ["trutv", "tru tv"]),
  net("hbo", ["hbo"]),
  net("syfy", ["syfy"]),
  net("e", ["e", "e entertainment"]),
  net("mtv", ["mtv"]),
  net("bet", ["bet"]),
  net("vh1", ["vh1"]),
  net("natgeo", ["national geographic", "nat geo", "natgeo"]),
  net("animalplanet", ["animal planet"]),
  net("travel", ["travel channel", "travel"]),
  net("weather", ["weather channel", "the weather channel"]),
  net("cnbc", ["cnbc"]),
  net("foxbusiness", ["fox business", "fox business network"]),
  net("golf", ["golf channel", "golf"], true),
  net("nbcsports", ["nbc sports", "nbc sports network", "nbcsn"], true),
  net("cbssports", ["cbs sports network", "cbs sports", "cbssn"], true),
  net("nbatv", ["nba tv"], true),
  net("mlbnetwork", ["mlb network"], true),
  net("nhlnetwork", ["nhl network"], true),
  net("btn", ["big ten network", "btn"], true),
  net("secn", ["sec network", "secn"], true),
  net("accn", ["acc network", "accn"], true),
  net("espnu", ["espnu", "espn u"], true),
  net("espnnews", ["espn news", "espnews"], true),
  net("pbs", ["pbs"]),
  net("cw", ["cw", "the cw"]),
  net("ion", ["ion", "ion television"]),
  net("tvland", ["tv land"]),
  net("oxygen", ["oxygen"]),
  net("showtime", ["showtime"]),
  net("starz", ["starz"]),
  net("hallmarkmm", ["hallmark movies mysteries", "hallmark mystery", "hallmark movies"]),
  net("courttv", ["court tv"]),
  net("tennis", ["tennis channel"], true),
  net("fs2", ["fs2", "fox sports 2"], true),
];

const RANK = new Map<string, number>(TOP_NETWORKS.map((network, index) => [network.id, index]));
const BY_NAME = new Map<string, Network>();
for (const network of TOP_NETWORKS) for (const name of network.names) if (!BY_NAME.has(name)) BY_NAME.set(name, network);

/** Broadcast networks whose local stations are the only way a provider carries them. */
const BROADCAST = new Set(["abc", "cbs", "nbc", "fox", "cw", "pbs"]);

const QUALITY = new Set(["hd", "fhd", "uhd", "sd", "4k", "raw", "hevc", "h264", "h265", "50fps", "60fps", "fps", "hdr", "hq", "lq", "backup", "alt"]);
const LEADING_COUNTRY = new Set(["usa", "us", "united", "states"]);
const FEED_SUFFIX = new Set(["east", "west", "pacific", "central", "mountain", "feed", "us", "usa"]);

/** A channel name as plain lowercase words: no marks, no quality tags ("ᴴᴰ", "FHD", "RAW"). */
function words(name: string): string[] {
  return name
    .normalize("NFKD")
    .toLowerCase()
    .replace(/&/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter((word) => word && !QUALITY.has(word));
}

function withoutLeadingCountry(list: string[]): string[] {
  let at = 0;
  while (at < list.length - 1 && LEADING_COUNTRY.has(list[at]!)) at += 1;
  return list.slice(at);
}

/** The network a channel's whole name says it is ("USA ESPN HD", "US| FOX NEWS ᴿᴬᵂ", "MS NOW (MSNBC)"), if any. */
export function networkOf(channelName: string): Network | undefined {
  const full = words(channelName);
  for (const list of [full, withoutLeadingCountry(full)]) {
    let candidate = list;
    if (candidate.length > 1 && FEED_SUFFIX.has(candidate[candidate.length - 1]!)) candidate = candidate.slice(0, -1);
    const hit = BY_NAME.get(candidate.join(" "));
    if (hit) return hit;
  }
  return undefined;
}

/** A local station of a broadcast network: "USA ABC (WDSU) New Orleans HD" → { abc, "new orleans" }. */
export function localOf(channelName: string): { network: string; market: string } | undefined {
  const list = withoutLeadingCountry(words(channelName));
  const head = list[0];
  if (!head || !BROADCAST.has(head)) return undefined;
  const market = list
    .slice(1)
    .filter((word) => !/^\d+$/.test(word) && !/^[kw][a-z]{2,3}$/.test(word) && !FEED_SUFFIX.has(word) && word !== "tv")
    .join(" ");
  return market ? { network: head, market } : undefined;
}

/** The market the viewer's own stations (favourites, recently watched) say they are in: the most common, or null. */
export function localMarketFrom(channelNames: readonly string[]): string | null {
  const counts = new Map<string, number>();
  for (const name of channelNames) {
    const local = localOf(name);
    if (local) counts.set(local.market, (counts.get(local.market) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestCount = 0;
  for (const [market, count] of counts) {
    if (count > bestCount) {
      best = market;
      bestCount = count;
    }
  }
  return best;
}

/** "24/7 The Big Bang Theory", "24-7 Friends", a "24/7 Drama" category: a loop, never live TV. */
export function isTwentyFourSeven(...texts: Array<string | undefined>): boolean {
  return texts.some((text) => !!text && /(^|[^a-z0-9])24\s*[/\-_ ]?\s*7(?![a-z0-9])/i.test(text));
}

const PACKAGE_WORD = /(^|[^a-z0-9])(teams?|events?|ppv|pay\s*per\s*view|replays?|vod|game\s*pass|season\s*pass|originals?)(?![a-z0-9])/i;
const EVENT_FEED = [
  /(^|[^a-z0-9])vs\.?(?![a-z0-9])/i,
  /(^|[^a-z0-9])\d{1,2}:\d{2}(?![0-9])/,
  /(^|[^a-z])(mon|tues?|wednes|thurs?|fri|satur|sun)day(?![a-z])/i,
  /\|\s*\d/,
];

/** An event or team-package channel: "NFL Teams: NBC Chiefs (KSHB)", "Serie A 09: Atalanta vs. Venezia | Monday 17:30". */
export function isPackageOrEvent(...texts: Array<string | undefined>): boolean {
  return texts.some((text) => !!text && (PACKAGE_WORD.test(text) || EVENT_FEED.some((pattern) => pattern.test(text))));
}

/** US only: a USA tag in the channel's category or name, or no country tag at all and a known US network's name. */
export function isUsChannel(channelName: string, categoryName: string | undefined, knownNetwork: boolean): boolean {
  if (isOtherCountry(channelName) || (categoryName && isOtherCountry(categoryName))) return false;
  if (isUsaName(channelName) || (categoryName && isUsaName(categoryName))) return true;
  return knownNetwork;
}

/** The viewer's country: for the US, isUsChannel; elsewhere a tag of theirs and no other country's, in the name or the category. */
export function isViewerChannel(channelName: string, categoryName: string | undefined, knownNetwork: boolean, viewer: string): boolean {
  if (viewer === "US") return isUsChannel(channelName, categoryName, knownNetwork);
  const tags = [countryOfName(channelName), categoryName ? countryOfName(categoryName) : null];
  if (tags.some((tag) => tag !== null && tag !== viewer)) return false;
  return tags.includes(viewer);
}

const STUDIO_SHOW = /\b(sportscenter|get up|first take|pardon the interruption|around the horn|highlights?|countdown|pre-?game|post-?game|talk|today|daily|morning|mornings|radio|news|show|podcast|rewind|classic|replay|encore)\b/i;
const EVENT_TITLE = /(\b(vs\.?|v\.?|at|@)\b|\blive\b|\b(nfl|nba|wnba|nhl|mlb|mls|ncaa|ufc|nascar|pga|f1|atp|wta|premier league|champions league)\b)/i;

/** Whether what is on is a live game or event (the 2x boost): sports by the guide's genre or a sports network, and an event's look. */
export function isLiveSports(program: { title: string; genre?: string }, network: Network | undefined): boolean {
  if (STUDIO_SHOW.test(program.title)) return false;
  if (!EVENT_TITLE.test(program.title)) return false;
  return /sport/i.test(program.genre ?? "") || network?.sports === true;
}

/** A channel that could be featured whenever it has something on: its network, once per channel list. */
export interface EligibleChannel<C> {
  channel: C;
  network: Network | null;
  local: { network: string; market: string } | null;
  /** A backup or alternate feed ("USA ESPN BACKUP"): it only wins when the real one has nothing on. */
  backup: boolean;
  /** Outside the US: the channel's name without its country tag, one tile per name ("UK| BBC One HD" → "bbc one"). */
  ownKey?: string;
}

/** "ESPN BACKUP", "FOX NEWS ALT": a spare feed of the same network. */
export function isBackupFeed(channelName: string): boolean {
  return /(^|[^a-z0-9])(backup|alt|alternate)(?![a-z0-9])/i.test(channelName);
}

/** The category's readable name: the guide derives it from the id the same way. */
export const categoryNameOf = (categoryId: string) => categoryId.replace(/^cat-/, "").replace(/-/g, " ");

const eligibleCache = new WeakMap<object, Map<string, EligibleChannel<unknown>[]>>();

/**
 * "UK| BBC One HD" → "bbc one", ":DAZN UK 37" → "dazn": the name's words without a tag of the
 * viewer's country or a feed number, so a provider's numbered feeds ("Sky Store Premiere 4, 9,
 * 21, 30", all showing the same film) are one tile.
 */
function ownKeyOf(channelName: string, viewer: string): string {
  const list = words(channelName);
  const kept = list.filter((word) => !/^\d+$/.test(word) && !(word.length <= 3 && countryOfName(`${word} x`) === viewer));
  return (kept.length > 0 ? kept : list).join(" ");
}

/**
 * The channels that could ever be featured: US, not a loop, not an event or package, and a
 * top network or a local station (outside the US: the viewer's country's, by tag). Worked out
 * once per channel list (10,000 names), not per tick: only these few hundred are asked what is on.
 */
export function eligibleChannels<C extends { name: string; categoryId: string }>(channels: readonly C[], viewer = "US"): EligibleChannel<C>[] {
  const byViewer = eligibleCache.get(channels as object) ?? new Map<string, EligibleChannel<unknown>[]>();
  const held = byViewer.get(viewer);
  if (held) return held as EligibleChannel<C>[];
  const result: EligibleChannel<C>[] = [];
  for (const channel of channels) {
    const categoryName = categoryNameOf(channel.categoryId);
    if (isTwentyFourSeven(channel.name, categoryName) || isPackageOrEvent(channel.name, categoryName)) continue;
    if (viewer !== "US") {
      if (!isViewerChannel(channel.name, categoryName, false, viewer)) continue;
      const network = networkOf(channel.name) ?? null;
      result.push({ channel, network, local: null, backup: isBackupFeed(channel.name), ownKey: ownKeyOf(channel.name, viewer) });
      continue;
    }
    const network = networkOf(channel.name) ?? null;
    const local = network ? null : localOf(channel.name) ?? null;
    if (!network && !local) continue;
    if (!isUsChannel(channel.name, categoryName, network !== null)) continue;
    result.push({ channel, network, local, backup: isBackupFeed(channel.name) });
  }
  byViewer.set(viewer, result as EligibleChannel<unknown>[]);
  eligibleCache.set(channels as object, byViewer);
  return result;
}

/** What a program needs for the rail: its title, and the guide's genre when it has one. */
export interface LiveProgram {
  title: string;
  genre?: string;
}

export interface LiveCandidate<C, P extends LiveProgram = LiveProgram> {
  eligible: EligibleChannel<C>;
  program: P;
}

export interface LivePick<C, P extends LiveProgram = LiveProgram> {
  channel: C;
  networkId: string;
  program: P;
  sports: boolean;
  score: number;
}

/** A stable number in [0, 1) from a string: the same channel and slot always shake the same way. */
function jitter(text: string): number {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return ((hash >>> 0) % 10_000) / 10_000;
}

export const LIVE_SLOT_MS = 30 * 60_000;

/**
 * The tiles: what each eligible channel has on now, one per network, scored by rank (the big
 * names weigh most), doubled for live sports, shaken by half hour so the rail turns over.
 * A local station counts only for the viewer's market, as its broadcast network.
 */
export function pickLiveNow<C extends { id: string }, P extends LiveProgram>(
  candidates: readonly LiveCandidate<C, P>[],
  options: { now: number; max: number; market?: string | null },
): LivePick<C, P>[] {
  const slot = Math.floor(options.now / LIVE_SLOT_MS);
  const best = new Map<string, LivePick<C, P>>();
  for (const { eligible, program } of candidates) {
    let networkId: string;
    let network: Network | undefined;
    if (eligible.network) {
      network = eligible.network;
      networkId = network.id;
    } else if (eligible.local && options.market && eligible.local.market === options.market) {
      networkId = eligible.local.network;
      network = RANK.has(networkId) ? TOP_NETWORKS[RANK.get(networkId)!] : undefined;
    } else if (eligible.ownKey) {
      networkId = `own:${eligible.ownKey}`;
    } else {
      continue;
    }
    // A broadcast network's national feed and a local station of it are one tile.
    const rank = RANK.get(networkId) ?? TOP_NETWORKS.length;
    const sports = isLiveSports(program, network);
    const base = 100 / (1 + rank / 12);
    const score = base * (sports ? 2 : 1) * (eligible.backup ? 0.25 : 1) * (0.5 + jitter(`${eligible.channel.id}|${slot}`));
    const pick: LivePick<C, P> = { channel: eligible.channel, networkId, program, sports, score };
    const held = best.get(networkId);
    if (!held || pick.score > held.score) best.set(networkId, pick);
  }
  return [...best.values()].sort((a, b) => b.score - a.score).slice(0, options.max);
}
